import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Info } from "lucide-react";
import { FaFileExcel } from "react-icons/fa";
import { Tooltip as InfoTooltip } from "react-tooltip";

const ROW_HEIGHT = 44; // px ~ per row
const VISIBLE_BODY_ROWS = 10;

const TableWrapper = styled.div`
  background-color: #f9f9f9;
  width: 100%;
  padding: 20px;
`;

const TitleWrapper = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-weight: bold;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const ScrollContainer = styled.div`
  max-height: ${ROW_HEIGHT * (VISIBLE_BODY_ROWS + 1)}px;
  overflow-y: auto;
  position: relative;
  border: 1px solid #ddd;
  border-radius: 6px;
`;

const ControlsRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-bottom: 14px;
  flex-wrap: wrap;
  align-items: center;
`;

const Input = styled.input`
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 14px;
  border: 1px solid #ccc;
  width: 300px;
`;

const ExportButton = styled.button`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background-color: #1d6f42;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background-color: #155e36;
  }

  svg {
    margin-right: 6px;
  }
`;

const StyledTable = styled.table`
  border-collapse: collapse;
  width: 100%;
  font-size: 0.7rem;

  th,
  td {
    border: 1px solid #ddd;
    text-align: center;
    padding: 12px 8px;
    line-height: 1.2;
  }

  th:nth-child(1),
  td:nth-child(1) {
    width: 36%;
  }
  th:nth-child(2),
  td:nth-child(2) {
    width: 20%;
  }
  th:nth-child(3),
  td:nth-child(3) {
    width: 22%;
  }
  th:nth-child(4),
  td:nth-child(4) {
    width: 22%;
  }

  th {
    background: #9848c7; /* zakupy color */
    color: #fff;
    position: sticky;
    top: 0;
    z-index: 2;
  }

  tr:nth-child(even) {
    background-color: #f9f9f9;
  }
  tbody tr:hover {
    background-color: #eef6ff;
  }
`;

const StatusText = styled.span`
  font-weight: bold;
  color: ${({ months }) => (months >= 6 ? "red" : "#333")};
`;

const InactiveSuppliers = ({ zakupyData }) => {
  const [inactiveSuppliers, setInactiveSuppliers] = useState([]);
  const [filterText, setFilterText] = useState("");

  const ymKey = (y, m) => `${y}-${String(m).padStart(2, "0")}`;

  useEffect(() => {
    if (!zakupyData || zakupyData.length === 0) return;

    // last purchase per supplier (name + NrDostawcy)
    const lastPurchases = {};
    zakupyData.forEach((row) => {
      const supplier = row.NazwaDostawcy || "Nieznany dostawca";
      const nr = (row.NrDostawcy || "").toString().trim();
      const year = parseInt(row.Rok, 10);
      const month = parseInt(row.Miesiac, 10);
      if (isNaN(year) || isNaN(month)) return;

      const key = ymKey(year, month);
      const supplierKey = `${supplier}||${nr}`;
      if (!lastPurchases[supplierKey] || lastPurchases[supplierKey] < key) {
        lastPurchases[supplierKey] = key;
      }
    });

    // latest month in dataset
    const latestKey = zakupyData.reduce((latest, row) => {
      const y = parseInt(row.Rok, 10);
      const m = parseInt(row.Miesiac, 10);
      if (isNaN(y) || isNaN(m)) return latest;
      const k = ymKey(y, m);
      return !latest || latest < k ? k : latest;
    }, null);

    if (!latestKey) return;
    const [latestYear, latestMonth] = latestKey.split("-").map(Number);

    const list = Object.entries(lastPurchases).map(([key, lastDate]) => {
      const [supplier, nr] = key.split("||");
      const [lastYear, lastMonth] = lastDate.split("-").map(Number);
      const monthsDiff =
        (latestYear - lastYear) * 12 + (latestMonth - lastMonth);
      return {
        supplier,
        nr,
        lastPurchase: lastDate,
        monthsInactive: monthsDiff,
      };
    });

    // only 6+ months inactive, sort by longest inactivity then name
    const filtered = list
      .filter((c) => c.monthsInactive >= 6)
      .sort(
        (a, b) =>
          b.monthsInactive - a.monthsInactive ||
          a.supplier.localeCompare(b.supplier)
      );

    setInactiveSuppliers(filtered);
  }, [zakupyData]);

  const displayedSuppliers = inactiveSuppliers.filter((item) => {
    const needle = filterText.trim().toLowerCase();
    if (!needle) return true;
    return (
      item.supplier.toLowerCase().includes(needle) ||
      (item.nr || "").toLowerCase().includes(needle)
    );
  });

  const exportToExcel = () => {
    const headers = [
      "Dostawca",
      "Nr dostawcy",
      "Ostatni zakup (YYYY-MM)",
      "Miesiące braku zakupów",
    ];
    const rows = displayedSuppliers.map((r) => [
      r.supplier,
      r.nr || "",
      r.lastPurchase,
      r.monthsInactive,
    ]);

    const sep = ";";
    const escape = (v) => {
      const s = String(v ?? "");
      return /[;"\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };

    const csv = [headers, ...rows]
      .map((row) => row.map(escape).join(sep))
      .join("\r\n");
    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });

    const now = new Date();
    const fname = `nieaktywni_dostawcy_${now.getFullYear()}-${String(
      now.getMonth() + 1
    ).padStart(2, "0")}.csv`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fname;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <TableWrapper>
      <TitleWrapper>
        Dostawcy, od których nie kupiliśmy od 6 miesięcy
        <div
          data-tooltip-id="inactive-suppliers-tooltip"
          style={{ cursor: "pointer" }}
        >
          <Info size={18} />
        </div>
      </TitleWrapper>

      <InfoTooltip
        id="inactive-suppliers-tooltip"
        place="right"
        style={{
          fontSize: "12px",
          fontFamily: "Arial, sans-serif",
          color: "#fff",
        }}
        content={
          <>
            <strong>Analiza nieaktywnych dostawców</strong>
            <br />
            Lista pokazuje dostawców, u których nie dokonano zakupu od{" "}
            <b>6 miesięcy lub dłużej</b>.
            <br />
            Szukaj po nazwie lub numerze dostawcy. Eksport dostępny do pliku
            CSV (Excel).
          </>
        }
      />

      <ControlsRow>
        <div>
          <label>Dostawca / Nr dostawcy: </label>
          <Input
            type="text"
            placeholder="Wpisz nazwę lub nr dostawcy…"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </div>

        <ExportButton onClick={exportToExcel}>
          <FaFileExcel />
          Eksportuj do Excel
        </ExportButton>
      </ControlsRow>

      <ScrollContainer>
        <StyledTable>
          <thead>
            <tr>
              <th>Dostawca</th>
              <th>Nr dostawcy</th>
              <th>Ostatni zakup</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {displayedSuppliers.length > 0 ? (
              displayedSuppliers.map((item, index) => (
                <tr key={`${item.supplier}-${item.nr}-${index}`}>
                  <td>{item.supplier}</td>
                  <td>{item.nr || "—"}</td>
                  <td>{item.lastPurchase}</td>
                  <td>
                    <StatusText months={item.monthsInactive}>
                      Brak zakupów {item.monthsInactive}+ miesięcy
                    </StatusText>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4">Brak dostawców dla tego filtra.</td>
              </tr>
            )}
          </tbody>
        </StyledTable>
      </ScrollContainer>
    </TableWrapper>
  );
};

export default InactiveSuppliers;
